import useGlobalContext from "hooks/useGlobalContext";
import { useCallback, useEffect, useMemo } from "react";
import useEventTracking from "hooks/useEventTracking";
import { useSelector } from "react-redux";
import { getOrder } from "store/order/selectors";
import { UIWhitelabelAncillary } from "@flights/ancillaries-universal";
import { getFlight } from "store/flightDetails/selectors";
import { getFlightNumber } from "utils/getFlightNumbers";
import useContextName from "hooks/useContextName";
import { getSearchSegments } from "store/search/selectors";

export type UIWhitelabelContext = Partial<ReturnType<typeof useContextName>>;

export const useTrackWhitelabelLoadBanner = (
  whitelabelAncillary: UIWhitelabelAncillary["ancillary"],
  hasButton: boolean,
  context: UIWhitelabelContext
) => {
  const { requestId } = useGlobalContext();
  const trackV2 = useEventTracking("whitelabel", requestId);
  const data = useGetTrackWhitelabelProps(whitelabelAncillary, hasButton, context);

  useEffect(() => {
    if (data) {
      trackV2("load_banner_whitelabel", data);
    }
  }, [data, trackV2]);
};

export const useTrackWhitelabelViewBanner = (
  whitelabelAncillary: UIWhitelabelAncillary["ancillary"],
  hasButton: boolean,
  inViewport: boolean,
  context: UIWhitelabelContext
) => {
  const { requestId } = useGlobalContext();
  const trackV2 = useEventTracking("whitelabel", requestId);
  const data = useGetTrackWhitelabelProps(whitelabelAncillary, hasButton, context);

  useEffect(() => {
    if (!inViewport || !data) {
      return;
    }
    trackV2("view_banner_whitelabel", data);
  }, [data, inViewport, requestId, trackV2, whitelabelAncillary]);
};

export const useWhitelabelClickBanner = (
  whitelabelAncillary: UIWhitelabelAncillary["ancillary"],
  context: UIWhitelabelContext
) => {
  const { requestId } = useGlobalContext();
  const trackV2 = useEventTracking("whitelabel", requestId);
  const data = useGetTrackWhitelabelProps(whitelabelAncillary, true, context);

  return useCallback(() => {
    if (data) {
      trackV2("click_banner_whitelabel", data);
    }
  }, [data, trackV2]);
};

// Post-booking tracking properties hook
const useGetPostBookingTrackWhitelabelProps = (
  whitelabelAncillary: UIWhitelabelAncillary["ancillary"],
  hasButton: boolean
) => {
  const order = useSelector(getOrder);
  const SUPPORTED_CURRENCIES = ["GBP", "EUR", "NOK"];
  const orderCurrency = order?.orderCurrency;
  const currency = orderCurrency && SUPPORTED_CURRENCIES.includes(orderCurrency.toUpperCase()) ? orderCurrency : "USD";

  return useMemo(
    () => ({
      product: whitelabelAncillary,
      orderid: order?.orderId ?? null,
      order_status: order?.orderStatus ?? null,
      airport_iata: order?.airOrder.flightSegments[0]?.departureAirport.code ?? null,
      flight_offer_token: null,
      action: hasButton ? "clickable" : "non-clickable",
      date_of_visit: order?.airOrder.flightSegments?.[0]?.departureTime?.split("T")[0] ?? null,
      time_of_visit: order?.airOrder.flightSegments?.[0]?.departureTime ?? null,
      flight_no: getFlightNumber(order?.airOrder.flightSegments?.[0]?.legs[0]) ?? null,
      placement: "banner",
      source: null, // "email" | "pushnotification"
      currency: currency
    }),
    [hasButton, order?.airOrder.flightSegments, order?.orderId, order?.orderStatus, whitelabelAncillary, currency]
  );
};

// Search results tracking properties hook
const useGetSearchResultsTrackWhitelabelProps = (
  whitelabelAncillary: UIWhitelabelAncillary["ancillary"],
  hasButton: boolean
) => {
  const searchSegments = useSelector(getSearchSegments);
  return useMemo(
    () => ({
      product: whitelabelAncillary,
      orderid: null,
      order_status: null,
      airport_iata: searchSegments[0]?.from[0]?.code ?? null,
      flight_offer_token: null,
      action: hasButton ? "clickable" : "non-clickable",
      date_of_visit: searchSegments[0]?.departureDate ?? null,
      time_of_visit: searchSegments[0]?.departureDate?.split("T")[1] ?? null,
      flight_no: null,
      placement: "banner",
      source: null // "email" | "pushnotification"
    }),
    [hasButton, searchSegments, whitelabelAncillary]
  );
};

// Flight details tracking properties hook
const useGetFlightDetailsTrackWhitelabelProps = (
  whitelabelAncillary: UIWhitelabelAncillary["ancillary"],
  hasButton: boolean
) => {
  const flight = useSelector(getFlight);
  return useMemo(
    () => ({
      product: whitelabelAncillary,
      orderid: null,
      order_status: null,
      airport_iata: flight?.segments?.[0]?.departureAirport.code ?? null,
      flight_offer_token: flight?.token ?? null,
      action: hasButton ? "clickable" : "non-clickable",
      date_of_visit: flight?.segments?.[0]?.departureTime?.split("T")[0] ?? null,
      time_of_visit: flight?.segments?.[0]?.departureTime ?? null,
      flight_no: getFlightNumber(flight?.segments?.[0]?.legs?.[0]) ?? null,
      placement: "banner",
      source: null // "email" | "pushnotification"
    }),
    [flight?.segments, flight?.token, hasButton, whitelabelAncillary]
  );
};

// Main hook that selects the appropriate tracking properties based on context
const useGetTrackWhitelabelProps = (
  whitelabelAncillary: UIWhitelabelAncillary["ancillary"],
  hasButton: boolean,
  context: UIWhitelabelContext
) => {
  const isPostBooking = context === "confirmation" || context === "pb_order_details";
  const isSearchResults = context === "search_results";
  const isCheckoutPayment = context === "checkout3";
  const isFlightDetails = context === "flight_details";

  const postBookingProps = useGetPostBookingTrackWhitelabelProps(whitelabelAncillary, hasButton);
  const searchResultsProps = useGetSearchResultsTrackWhitelabelProps(whitelabelAncillary, hasButton);
  const flightDetailsProps = useGetFlightDetailsTrackWhitelabelProps(whitelabelAncillary, hasButton);

  if (isPostBooking || isCheckoutPayment) {
    return postBookingProps;
  } else if (isSearchResults) {
    return searchResultsProps;
  } else if (isFlightDetails) {
    return flightDetailsProps;
  } else {
    return null;
  }
};
